import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useState, useCallback, useMemo } from "react";
import { ImagePreviewOverlay } from "../ImagePreviewOverlay.js";
import { useIsMobile } from "../ConciergeChatAreaV2/useIsMobile.js";
import { TextPart } from "./TextPart.js";
import { isCallState, isOutputState } from "./types.js";
import { FilterResultsPart } from "./FilterResultsPart.js";
import { DailyDealsPart } from "./DailyDealsPart.js";
import { SurveysPart } from "./SurveysPart.js";
import { getMergedStoreLocatorInfo } from "./StoreLocatorPart.js";
import { ProductDetailsPart } from "./ProductDetailsPart.js";
import { PdpProductInfoPart } from "./PdpProductInfoPart.js";
import { PromptLoginPart } from "./PromptLoginPart.js";
import { MfWidgetLoader } from "./MfWidgetLoader.js";
import { SupportTicketCardFromCreateTicket, SupportTicketCardFromRouteToSupport } from "./SupportTicketCard.js";
import { SupportTicketsListPart } from "./SupportTicketsListPart.js";
import { SupportTicketStatusPart } from "./SupportTicketStatusPart.js";
import { HumanReplyPart } from "./HumanReplyPart.js";
import { VoiceWidgetInTextChat } from "../VoiceWidgetInTextChat.js";
function isVoiceToolResultOutput(output) {
  if (!output || typeof output !== "object") return false;
  const o = output;
  return typeof o.narrator !== "undefined" && typeof o.durationMs !== "undefined";
}
function ConciergePartItem({
  part,
  allParts,
  onProductClick,
  isStreaming,
  pdpStacklineSku,
  isOnChatWidget,
  apiBaseUrl
}) {
  var _a, _b, _c, _d, _e, _f, _g;
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const closePreview = useCallback(() => setPreviewImageUrl(null), []);
  const isMobile = useIsMobile();
  const sourcesMap = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    allParts.forEach((p) => {
      if (p.type === "source-url") {
        const sp = p;
        map.set(String(sp.sourceId), { url: sp.url, title: sp.title });
      } else if (p.type === "data-source-url") {
        const dp = p.data;
        if (dp == null ? void 0 : dp.sourceId) map.set(String(dp.sourceId), { url: dp.url, title: dp.title });
      }
    });
    return map;
  }, [allParts]);
  if (typeof part.type === "string" && part.type.startsWith("data-widget-") && part.type !== "data-widget-tool-running" && part.type !== "data-widget-clear") {
    return /* @__PURE__ */ jsx(VoiceWidgetInTextChat, { type: part.type, data: part.data });
  }
  if (typeof part.type === "string" && part.type.startsWith("tool-") && isVoiceToolResultOutput(part.output)) {
    return null;
  }
  switch (part.type) {
    case "file":
      if (((_a = part.mediaType) == null ? void 0 : _a.startsWith("image/")) && part.url) {
        const imageCount = allParts.filter(
          (p) => {
            var _a2;
            return p.type === "file" && ((_a2 = p.mediaType) == null ? void 0 : _a2.startsWith("image/"));
          }
        ).length;
        const imgSize = imageCount === 1 ? isMobile ? 250 : 256 : isMobile ? 96 : 128;
        return /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("div", { className: "tw:flex tw:justify-end tw:w-full tw:mb-2", children: /* @__PURE__ */ jsx(
            "div",
            {
              onClick: () => setPreviewImageUrl(part.url),
              style: {
                width: `${imgSize}px`,
                height: `${imgSize}px`,
                borderRadius: "12px",
                overflow: "hidden",
                backgroundColor: "#f9f9f9",
                cursor: "zoom-in"
              },
              children: /* @__PURE__ */ jsx(
                "img",
                {
                  src: part.url,
                  alt: part.filename || "Uploaded image",
                  style: {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    mixBlendMode: "multiply"
                  }
                }
              )
            }
          ) }),
          previewImageUrl && /* @__PURE__ */ jsx(ImagePreviewOverlay, { src: previewImageUrl, onClose: closePreview })
        ] });
      }
      if (part.url) {
        return /* @__PURE__ */ jsx(
          "a",
          {
            href: part.url,
            target: "_blank",
            rel: "noopener noreferrer",
            className: "tw:text-sm tw:underline tw:text-blue-600",
            children: part.filename || "Attached file"
          }
        );
      }
      return null;
    case "text":
      return /* @__PURE__ */ jsx(TextPart, { text: part.text || "", sourcesMap });
    case "tool-analyze_query":
    case "tool-search_products":
      return null;
    case "tool-filter_results":
      return /* @__PURE__ */ jsx(FilterResultsPart, { part });
    case "tool-product_carousels":
    case "tool-organize_results": {
      if (allParts.some((p) => p.type === "data-product-carousels" || p.type === "data-section")) return null;
      if (isCallState(part.state) && isStreaming) return null;
      return /* @__PURE__ */ jsx(
        MfWidgetLoader,
        {
          widgetId: "product-carousels",
          toolOutput: part.output ?? null,
          partState: part.state
        }
      );
    }
    case "data-product-carousels":
    case "data-section":
      return /* @__PURE__ */ jsx(
        MfWidgetLoader,
        {
          widgetId: "product-carousels",
          dataParts: [{ type: part.type, data: part.data }]
        }
      );
    case "tool-get_daily_deals":
      return /* @__PURE__ */ jsx(DailyDealsPart, { part, onProductClick });
    case "tool-get_surveys":
      return /* @__PURE__ */ jsx(SurveysPart, { part, allParts });
    case "tool-show_store_locator": {
      const mergedInfo = getMergedStoreLocatorInfo(allParts);
      if (mergedInfo) {
        const partIndex = allParts.indexOf(part);
        if (partIndex !== mergedInfo.renderIndex) return null;
      }
      if (isCallState(part.state) && isStreaming) return null;
      const slOutput = part.output;
      if (!slOutput || slOutput.error || slOutput.isOnlineOnly) return null;
      const skus = (mergedInfo == null ? void 0 : mergedInfo.allSkus) ?? (slOutput == null ? void 0 : slOutput.stacklineSkus) ?? [];
      if (!slOutput.hasStores || skus.length === 0) return null;
      return /* @__PURE__ */ jsx(
        MfWidgetLoader,
        {
          widgetId: "store-locator",
          toolInput: part.input ?? null,
          toolOutput: { ...slOutput, stacklineSkus: skus },
          partState: part.state
        }
      );
    }
    case "tool-get_product_details":
      if ((_b = part.input) == null ? void 0 : _b.silent) return null;
      return /* @__PURE__ */ jsx(ProductDetailsPart, { part, onProductClick, pdpStacklineSku });
    case "tool-compare_products": {
      if (isCallState(part.state) && isStreaming) return null;
      const cpOutput = part.output;
      if (!((_c = cpOutput == null ? void 0 : cpOutput.products) == null ? void 0 : _c.length) || cpOutput.products.length < 2) return null;
      return /* @__PURE__ */ jsx(
        MfWidgetLoader,
        {
          widgetId: "compare-products",
          toolInput: part.input ?? null,
          toolOutput: cpOutput,
          partState: part.state
        }
      );
    }
    case "tool-show_featured_product": {
      if (allParts.some((p) => p.type === "data-pdp-product-info")) return null;
      if (isCallState(part.state) && isStreaming) return null;
      if (!isOutputState(part.state) && isStreaming) return null;
      return /* @__PURE__ */ jsx(
        MfWidgetLoader,
        {
          widgetId: "featured-product",
          toolOutput: part.output ?? null,
          partState: part.state,
          onProductClick
        }
      );
    }
    case "data-product-highlight":
      return /* @__PURE__ */ jsx(
        MfWidgetLoader,
        {
          widgetId: "product-highlight",
          toolOutput: part.data ?? null,
          dataParts: [{ type: "data-product-highlight", data: part.data }],
          onProductClick
        }
      );
    case "data-pdp-product-info":
      if (isOnChatWidget && ((_d = part.data) == null ? void 0 : _d.autoInjected)) return null;
      return /* @__PURE__ */ jsx(PdpProductInfoPart, { part });
    case "source-url":
    case "data-source-url":
      return null;
    case "data-prompt-login":
      return /* @__PURE__ */ jsx(PromptLoginPart, { reason: (_e = part.data) == null ? void 0 : _e.reason, isStreaming });
    case "tool-prompt_login":
      if (allParts.some((p) => p.type === "data-prompt-login")) return null;
      return /* @__PURE__ */ jsx(PromptLoginPart, { reason: (_f = part.input) == null ? void 0 : _f.reason, isStreaming });
    case "tool-show_order_history": {
      if (isCallState(part.state) && isStreaming) return null;
      if (!isOutputState(part.state) && isStreaming) return null;
      const ohOutput = part.output;
      if (!((_g = ohOutput == null ? void 0 : ohOutput._metadata) == null ? void 0 : _g.isAuthenticated)) return null;
      return /* @__PURE__ */ jsx(
        MfWidgetLoader,
        {
          widgetId: "order-history",
          toolInput: part.input ?? null,
          toolOutput: ohOutput,
          partState: part.state
        }
      );
    }
    case "tool-route_to_support": {
      return /* @__PURE__ */ jsx(SupportTicketCardFromRouteToSupport, { output: part.output });
    }
    case "tool-create_ticket":
      return /* @__PURE__ */ jsx(SupportTicketCardFromCreateTicket, { output: part.output });
    case "tool-request_live_agent":
      return null;
    case "tool-end_live_agent":
      return null;
    case "tool-search_related_tickets":
    case "tool-lookup_tickets":
      return /* @__PURE__ */ jsx(SupportTicketsListPart, { output: part.output });
    case "tool-get_ticket_status":
      return /* @__PURE__ */ jsx(SupportTicketStatusPart, { output: part.output });
    case "tool-delegate_to_support_agent":
      return null;
    case "data-human-reply":
      return /* @__PURE__ */ jsx(HumanReplyPart, { data: part.data });
    case "data-live-takeover-active":
    case "data-live-takeover-started":
    case "data-live-takeover-ended":
    case "data-takeover-team-changed":
    case "data-user-message-mirrored":
    case "data-user-message-delivery-failed":
    case "data-ticket-status-updated":
      return null;
    case "tool-search_faq":
    case "tool-escalate_to_support":
    case "tool-ask_user_for_information":
    case "tool-query_knowledge_base":
    case "tool-search_review_insights":
      return null;
    case "tool-invocation": {
      const ti = part.toolInvocation;
      if (!(ti == null ? void 0 : ti.toolName)) return null;
      const expanded = {
        type: `tool-${ti.toolName}`,
        state: ti.state,
        toolCallId: ti.toolCallId,
        toolName: ti.toolName,
        input: ti.args,
        output: ti.result
      };
      return /* @__PURE__ */ jsx(
        ConciergePartItem,
        {
          part: expanded,
          allParts,
          onProductClick,
          isStreaming,
          pdpStacklineSku,
          isOnChatWidget,
          apiBaseUrl
        }
      );
    }
    default:
      return null;
  }
}
export {
  ConciergePartItem
};
//# sourceMappingURL=ConciergePartItem.js.map
